// /reports/sales — Sales Report (PAGES.md §3.40)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { SalesWaveChart } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { DailySalesRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Row = DailySalesRow & { id: string };

// Format an ISO date or 'YYYY-MM-DD' string as 'DD MMM YYYY' in IST.
// Accepts both raw 'YYYY-MM-DD' (post-fix) and legacy 'YYYY-MM-DDTHH:mm:ss.sssZ'.
function formatDayLong(raw: string): string {
  const ymd = raw.length >= 10 ? raw.slice(0, 10) : raw;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return raw;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d).padStart(2, '0')} ${months[m - 1]} ${y}`;
}

function formatDayShort(raw: string): string {
  const ymd = raw.length >= 10 ? raw.slice(0, 10) : raw;
  const [, m, d] = ymd.split('-').map(Number);
  if (!m || !d) return raw;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d).padStart(2, '0')} ${months[m - 1]}`;
}

// Cash rounding is signed: −0.250 when a bill was rounded down.
function formatSigned(paise: number, currency: CurrencyCode): string {
  if (paise === 0) return formatPaise(0, currency);
  return `${paise < 0 ? '−' : '+'}${formatPaise(Math.abs(paise), currency)}`;
}

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    { key: 'date', header: 'Date', render: (_v, row) => <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 tabular-nums">{formatDayLong(row.date)}</span> },
    { key: 'bill_count', header: 'Bills', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.bill_count.toLocaleString('en-IN')}</span> },
    { key: 'gross_sales', header: 'Gross Sales', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.gross_sales, currency)}</span> },
    { key: 'net_sales', header: 'Net Sales', numeric: true, render: (_v, row) => <span className="text-sm tabular-nums">{formatPaise(row.net_sales, currency)}</span> },
    { key: 'rounding', header: 'Rounding', numeric: true, render: (_v, row) => <span className="text-sm tabular-nums text-neutral-500 dark:text-neutral-400">{formatSigned(row.rounding ?? 0, currency)}</span> },
    { key: 'amount_collected', header: 'Amount Collected', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums text-success-700 dark:text-success-400">{formatPaise(row.amount_collected ?? row.net_sales, currency)}</span> },
    { key: 'avg_order_value', header: 'Avg Order', numeric: true, render: (_v, row) => <span className="text-sm tabular-nums">{formatPaise(row.avg_order_value, currency)}</span> },
    {
      key: 'growth_pct',
      header: 'Growth',
      numeric: true,
      render: (_v, row) => {
        const g = row.growth_pct ?? 0;
        const cls = g > 0 ? 'text-success-600' : g < 0 ? 'text-danger-600' : 'text-neutral-500';
        return <span className={`text-sm font-semibold tabular-nums ${cls}`}>{g > 0 ? '+' : ''}{g.toFixed(1)}%</span>;
      },
    },
  ];
}

export function SalesReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'sales',
    filter,
    queryFn: (f) => reportsApi.getSalesDailySummary(f),
  });

  const rows: Row[] = useMemo(
    () => (data?.daily ?? []).map((r) => ({ ...r, id: r.date })),
    [data],
  );

  // Chart values are in paise — SalesWaveChart formats with the currency's
  // subunit divisor itself. Sort ascending so the line reads left-to-right.
  const waveData = useMemo(
    () =>
      [...(data?.daily ?? [])]
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(-14)
        .map((r) => ({ label: formatDayShort(r.date), today: r.net_sales, yesterday: 0 })),
    [data],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `sales-report-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Date', get: (r: Row) => r.date },
          { header: 'Bills', get: (r: Row) => r.bill_count },
          { header: `Gross Sales (${symbol})`, get: (r: Row) => (r.gross_sales / subunit).toFixed(csvDecimals) },
          { header: `Net Sales (${symbol})`, get: (r: Row) => (r.net_sales / subunit).toFixed(csvDecimals) },
          { header: `Rounding (${symbol})`, get: (r: Row) => ((r.rounding ?? 0) / subunit).toFixed(csvDecimals) },
          { header: `Amount Collected (${symbol})`, get: (r: Row) => ((r.amount_collected ?? r.net_sales) / subunit).toFixed(csvDecimals) },
          { header: `Avg Order Value (${symbol})`, get: (r: Row) => (r.avg_order_value / subunit).toFixed(csvDecimals) },
          { header: 'Growth %', get: (r: Row) => r.growth_pct ?? 0 },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(
        fmt,
        'Orio360 Sales Report',
        `Total Sales: ${formatPaise(data?.total_sales ?? 0, currency)} • ${data?.bill_count ?? 0} bills • Period ${filter.range.from} → ${filter.range.to}`,
      );
    }
  };

  return (
    <ReportPageShell
      title="Sales Report"
      subtitle="Detailed revenue trend with daily breakdown and growth comparison."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Sales' }]}
      accent="brand-indigo"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={
        <ReportFilterBar
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          locationId={locationId}
          onLocationChange={setLocationId}
        />
      }
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={6} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'total', title: 'Total Sales', value: formatPaise(data?.total_sales ?? 0, currency), tone: 'brand', icon: KpiIcons.rupee, trend: { value: `${Math.abs(data?.growth_pct ?? 0).toFixed(1)}%`, direction: (data?.growth_pct ?? 0) >= 0 ? 'up' : 'down' } },
              { id: 'rounding', title: 'Rounding', value: formatSigned(data?.rounding ?? 0, currency), tone: 'violet', icon: KpiIcons.rupee },
              { id: 'collected', title: 'Amount Collected', value: formatPaise(data?.amount_collected ?? data?.total_sales ?? 0, currency), tone: 'success', icon: KpiIcons.rupee },
              { id: 'bills', title: 'Bill Count', value: (data?.bill_count ?? 0).toLocaleString('en-IN'), tone: 'indigo', icon: KpiIcons.receipt },
              { id: 'aov', title: 'Avg Order Value', value: formatPaise(data?.avg_order_value ?? 0, currency), tone: 'violet', icon: KpiIcons.cart },
              { id: 'growth', title: 'Growth vs Prev', value: `${(data?.growth_pct ?? 0).toFixed(1)}%`, tone: (data?.growth_pct ?? 0) >= 0 ? 'success' : 'danger', icon: (data?.growth_pct ?? 0) >= 0 ? KpiIcons.trendUp : KpiIcons.trendDown },
            ]}
          />

          {waveData.length > 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-5 mb-6 animate-slide-up">
              <SalesWaveChart data={waveData} currency={currency} title={`Daily Net Sales (last 14 days, ${symbol})`} />
            </div>
          ) : null}

          <ReportTable
            title="Daily Breakdown"
            columns={columns}
            data={rows}
            emptyTitle="No sales data for selected period"
            emptyDescription="Try selecting a wider date range or a different location."
            totalCount={rows.length}
          />
        </>
      )}
    </ReportPageShell>
  );
}
